import * as React from 'react';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import EditOutlined from '@mui/icons-material/EditOutlined';

export interface InlineNameFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder: string;
  label: string;
  /** Shown under the field and as its error state once `showError` is true. */
  error?: string;
  showError?: boolean;
}

/**
 * The entity name, edited where it is shown: an always-visible field styled as the page title. Replaces the name field the classic editor kept in the left panel.
 */
export default function InlineNameField({
  value,
  onChange,
  onBlur,
  placeholder,
  label,
  error,
  showError,
}: InlineNameFieldProps) {
  const invalid = Boolean(showError && error);
  return (
    <Tooltip title={invalid ? error : ''} open={invalid ? undefined : false} placement='bottom-start'>
      <InputBase
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        error={invalid}
        fullWidth
        slotProps={{ input: { 'aria-label': label, 'aria-invalid': invalid, title: value } }}
        endAdornment={<EditOutlined fontSize='small' sx={{ color: 'text.disabled', ml: 0.5, pointerEvents: 'none' }} />}
        sx={(theme) => ({
          fontSize: theme.typography.h6.fontSize,
          fontWeight: 600,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          border: 1,
          borderColor: invalid ? 'error.main' : 'divider',
          bgcolor: 'background.default',
          transition: theme.transitions.create(['border-color', 'background-color']),
          '& input': { textOverflow: 'ellipsis' },
          '&:hover': { borderColor: invalid ? 'error.main' : 'text.secondary' },
          '&.Mui-focused': {
            borderColor: invalid ? 'error.main' : 'primary.main',
            boxShadow: `0 0 0 3px ${theme.palette.action.focus}`,
          },
        })}
      />
    </Tooltip>
  );
}
